import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Receipt, ReceiptText, Trash2, Ban, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useApiFeedback } from '../../../lib/useApiFeedback';
import { inputCls } from '../../../components/admin/billing/shared';
import { StatusStepper } from '../../../components/admin/jobcards/StatusStepper';
import { TechnicianPicker } from '../../../components/admin/jobcards/TechnicianPicker';
import { JobCardLines, labourLine } from '../../../components/admin/jobcards/JobCardLines';
import { StatusPill, vehicleLabel } from '../../../components/admin/jobcards/shared';
import { JobCardHandover } from '../../../components/admin/jobcards/JobCardHandover';
import { shareJobCard } from '../../../lib/jobCardShare';
import { useDeviceDraft } from '../../../lib/useDeviceDraft';
import { DraftBar } from '../../../components/DraftBar';

const EMPTY = { customer_id: '', vehicle_id: '', odometer_km: '', promised_date: '', complaint: '', notes: '', technician_ids: [], status: 'open', items: [labourLine()] };

const Label = ({ children }) => <span className="mb-1 block text-[11px] uppercase tracking-wider text-slate-500">{children}</span>;

const fromCard = (c) => ({
  customer_id: c.customer_id, vehicle_id: c.vehicle_id || '', odometer_km: c.odometer_km ?? '',
  promised_date: c.promised_date || '', complaint: c.complaint || '', notes: c.notes || '',
  technician_ids: c.technician_ids || [], status: c.status, items: c.items || [],
});

const toPayload = (f) => ({
  ...f,
  odometer_km: f.odometer_km === '' ? null : Number(f.odometer_km),
  status: f.status === 'invoiced' ? 'ready' : f.status,
  items: f.items.map(({ _stock, ...it }) => ({ ...it, qty: Number(it.qty) || 0, rate: Number(it.rate) || 0, gst_rate: Number(it.gst_rate) || 0 })),
});

const InvoicedBanner = ({ invoice }) => {
  const to = invoice.kind === 'gst' ? `/admin/gst/invoices/${invoice.id}` : `/admin/billing/invoices/${invoice.id}`;
  return (
    <Link to={to} className="flex items-center justify-between rounded-xl border btn-ink px-4 py-3 text-sm" data-testid="jc-invoice-link">
      <span>Billed on {invoice.kind === 'gst' ? 'GST invoice' : 'invoice'} <b>{invoice.number}</b>. This card is now read-only.</span>
      <ExternalLink size={15} />
    </Link>
  );
};

export default function JobCardForm() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { authHeader } = useAuth();
  const { hasFeature } = useTenant();
  const { showError, showSuccess } = useApiFeedback();
  const [form, setForm] = useState(EMPTY);
  const [card, setCard] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    if (isNew) return undefined;
    let alive = true;
    // Only the first answer fills the form, so a late duplicate request can
    // never wipe out what someone has started typing.
    axios.get(`${API}/job-cards/${id}`, { headers: authHeader() })
      .then(({ data }) => { if (alive) { alive = false; setCard(data); setForm(fromCard(data)); } })
      .catch(showError);
    return () => { alive = false; };
  }, [id, isNew, authHeader, showError]);

  useEffect(() => {
    axios.get(`${API}/billing/customers`, { headers: authHeader() }).then((r) => setCustomers(r.data || [])).catch(() => {});
    axios.get(`${API}/job-cards/technicians`, { headers: authHeader() }).then((r) => setStaff(r.data || [])).catch(() => {});
  }, [authHeader]);

  const draft = useDeviceDraft(`jobcard:${isNew ? 'new' : id}`, form, setForm, isNew || !!card);
  const locked = card?.status === 'invoiced';
  const customer = useMemo(() => customers.find((c) => c.id === form.customer_id), [customers, form.customer_id]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const warn = (data) => (data?.stock_warnings || []).forEach((w) => toast.warning(`Running low: ${w}`));

  const save = async () => {
    if (!form.customer_id) { showError('Pick a customer first.'); return null; }
    setBusy('save');
    try {
      const { data } = isNew
        ? await axios.post(`${API}/job-cards`, toPayload(form), { headers: authHeader() })
        : await axios.put(`${API}/job-cards/${id}`, toPayload(form), { headers: authHeader() });
      draft.clear();
      showSuccess(`Job card ${data.number} saved.`); warn(data);
      if (data.status === 'ready' && card?.status !== 'ready' && data.customer_snapshot?.phone) {
        toast('Car marked ready', { description: 'Let the customer know it is ready for pickup.', duration: 10000,
          action: { label: 'Send ready card', onClick: () => shareJobCard(data, authHeader, 'ready') } });
      }
      setCard(data); setForm(fromCard(data));
      if (isNew) navigate(`/admin/job-cards/${data.id}`, { replace: true });
      return data;
    } catch (e) { showError(e); return null; } finally { setBusy(''); }
  };

  const invoice = async (kind) => {
    const saved = await save();
    if (!saved) return;
    setBusy(kind);
    try {
      const { data } = await axios.post(`${API}/job-cards/${saved.id}/invoice`, { kind }, { headers: authHeader() });
      showSuccess(`Invoice ${data.invoice.number} created.`); warn(data);
      navigate(kind === 'gst' ? `/admin/gst/invoices/${data.invoice.id}` : `/admin/billing/invoices/${data.invoice.id}`);
    } catch (e) { showError(e); } finally { setBusy(''); }
  };

  const deliver = async () => {
    setBusy('deliver');
    try {
      const { data } = await axios.post(`${API}/job-cards/${id}/delivered`, {}, { headers: authHeader() });
      setCard(data); showSuccess('Marked delivered. The thank-you follow-up is due tomorrow.');
    } catch (e) { showError(e); } finally { setBusy(''); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this job card? Any parts on it go back on the shelf.')) return;
    try { await axios.delete(`${API}/job-cards/${id}`, { headers: authHeader() }); navigate('/admin/job-cards'); } catch (e) { showError(e); }
  };

  return (
    <div className="min-h-full space-y-4 bg-slate-50 p-4 pb-28 sm:p-6" data-testid="job-card-form" onInputCapture={draft.touch} onChangeCapture={draft.touch} onClickCapture={draft.touch}>
      <DraftBar draft={draft} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Link to="/admin/job-cards" className="rounded p-1.5 hover:bg-slate-200" data-testid="jc-back"><ArrowLeft size={16} /></Link>
          <h1 className="text-xl font-bold text-slate-900 sm:text-2xl" data-testid="jc-title">{isNew ? 'New job card' : card?.number || 'Job card'}</h1>
          {card && <StatusPill status={card.status} testid="jc-status-pill" />}
        </div>
        {!locked && (
          <div className="flex flex-wrap items-center gap-2">
            {!isNew && form.status !== 'cancelled' && (
              <button type="button" onClick={() => set('status', 'cancelled')} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 hover:bg-slate-100" data-testid="jc-cancel-btn"><Ban size={14} /> Cancel card</button>
            )}
            {!isNew && <button type="button" onClick={remove} className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs text-red-600 hover:bg-red-50" data-testid="jc-delete-btn"><Trash2 size={14} /> Delete</button>}
            <button type="button" onClick={save} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60" data-testid="jc-save-btn">
              {busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
            </button>
          </div>
        )}
      </div>

      {locked && card?.invoice && <InvoicedBanner invoice={card.invoice} />}
      {form.status === 'cancelled' && !locked && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="jc-cancelled-note">
          Cancelled. Save to put its parts back on the shelf, or pick a stage below to reopen it.
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <Label>Stage</Label>
        <StatusStepper status={form.status === 'cancelled' ? '' : (card?.status === 'invoiced' ? 'invoiced' : form.status)} locked={locked} onChange={(s) => set('status', s)} />
      </div>

      {card && card.status !== 'cancelled' && (
        <JobCardHandover card={card} busy={busy === 'deliver'} onDeliver={deliver} onSend={() => shareJobCard(card, authHeader, 'ready')} />
      )}

      <div className="grid gap-4 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-2">
        <label className="block"><Label>Customer</Label>
          <select value={form.customer_id} disabled={locked} onChange={(e) => setForm((f) => ({ ...f, customer_id: e.target.value, vehicle_id: '' }))} className={inputCls} data-testid="jc-customer">
            <option value="">Pick a customer</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ''}</option>)}
          </select>
        </label>
        <label className="block"><Label>Vehicle</Label>
          <select value={form.vehicle_id} disabled={locked || !customer} onChange={(e) => set('vehicle_id', e.target.value)} className={inputCls} data-testid="jc-vehicle">
            <option value="">{customer?.vehicles?.length ? 'Pick a vehicle' : 'No vehicles on file'}</option>
            {(customer?.vehicles || []).map((v) => <option key={v.id} value={v.id}>{vehicleLabel(v)}</option>)}
          </select>
        </label>
        <label className="block"><Label>Odometer (km)</Label>
          <input type="number" min="0" value={form.odometer_km} disabled={locked} onChange={(e) => set('odometer_km', e.target.value)} className={inputCls} data-testid="jc-odometer" />
        </label>
        <label className="block"><Label>Promised by</Label>
          <input type="date" value={form.promised_date} disabled={locked} onChange={(e) => set('promised_date', e.target.value)} className={inputCls} data-testid="jc-promised" />
        </label>
        <label className="block md:col-span-2"><Label>What the customer reported</Label>
          <textarea rows={2} value={form.complaint} disabled={locked} onChange={(e) => set('complaint', e.target.value)} className={inputCls} placeholder="e.g. Squeal when braking, AC not cooling" data-testid="jc-complaint" />
        </label>
        <div className="md:col-span-2"><Label>Technicians</Label>
          <TechnicianPicker staff={staff} value={form.technician_ids} disabled={locked} onChange={(v) => set('technician_ids', v)} />
        </div>
      </div>

      <JobCardLines items={form.items} locked={locked} onChange={(items) => set('items', items)}
        vehicle={(customer?.vehicles || []).find((v) => v.id === form.vehicle_id)} />

      <label className="block rounded-xl border border-slate-200 bg-white p-4"><Label>Internal notes</Label>
        <textarea rows={2} value={form.notes} disabled={locked} onChange={(e) => set('notes', e.target.value)} className={inputCls} data-testid="jc-notes" />
      </label>

      {!locked && form.status !== 'cancelled' && (
        <div className="flex flex-wrap items-center justify-end gap-2 rounded-xl border border-slate-200 bg-white p-4" data-testid="jc-invoice-actions">
          <span className="mr-auto text-sm text-slate-600">Done with the car? Turn this card into a bill. Its parts move across without leaving the shelf twice.</span>
          <button type="button" onClick={() => invoice('invoice')} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-lg btn-ink px-4 py-2 text-sm font-semibold disabled:opacity-60" data-testid="jc-make-invoice-btn">
            {busy === 'invoice' ? <Loader2 size={14} className="animate-spin" /> : <Receipt size={14} />} Create invoice
          </button>
          {hasFeature('gst') && (
            <button type="button" onClick={() => invoice('gst')} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60" data-testid="jc-make-gst-invoice-btn">
              {busy === 'gst' ? <Loader2 size={14} className="animate-spin" /> : <ReceiptText size={14} />} Create GST invoice
            </button>
          )}
        </div>
      )}
    </div>
  );
}
